import { Component } from '@theme/component';

/** @extends {Component} */
class TabbedCollectionsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.panels = [...this.querySelectorAll('[data-tabbed-collections-panel]')];
    this.tablist = this.querySelector('[role="tablist"]');
    const panelTabs = this.panels.map((panel) => panel.querySelector('[data-tabbed-collections-tab]'));
    // The theme editor can reconnect this component after the controls have already been moved into the tablist.
    this.tabs = panelTabs.includes(null)
      ? [...(this.tablist?.querySelectorAll('[data-tabbed-collections-tab]') || [])]
      : panelTabs;

    if (!this.tablist || !this.tabs.length || this.tabs.length !== this.panels.length) return;

    // Theme blocks render as panel children; move their controls into the shared tablist for valid tab semantics.
    this.tabs.forEach((tab) => this.tablist.append(tab));
    const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#activate(selectedIndex >= 0 ? selectedIndex : 0);

    this.addEventListener('click', this.#handleClick);
    this.tablist.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.tablist?.removeEventListener('keydown', this.#handleKeydown);
    super.disconnectedCallback();
  }

  #handleClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    this.#activate(this.tabs.indexOf(tab));
  };

  #handleKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    const keys = {
      ArrowLeft: currentIndex - 1,
      ArrowRight: currentIndex + 1,
      Home: 0,
      End: this.tabs.length - 1,
    };
    if (!(event.key in keys)) return;

    event.preventDefault();
    const index = (keys[event.key] + this.tabs.length) % this.tabs.length;
    this.#activate(index, true);
  };

  #activate(index, moveFocus = false) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      panel.hidden = !selected;
      panel.toggleAttribute('inert', !selected);
    });

    const selectedTab = this.tabs[index];
    selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    if (moveFocus) selectedTab.focus();

    // Revealing a hidden panel changes its carousel width; resize observers then recalculate slide dimensions.
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }
}

if (!customElements.get('tabbed-collections-component')) {
  customElements.define('tabbed-collections-component', TabbedCollectionsComponent);
}
